import React, { useState } from 'react';
import { Card, Table, Button, Input, Space, Tag, Typography, Row, Col, Select, DatePicker, Statistic } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined, DollarOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface FinanceRecord {
  key: string;
  id: string;
  recordType: 'income' | 'expense' | 'refund';
  amount: number;
  description: string;
  merchantName: string;
  orderNumber: string;
  paymentMethod: string;
  status: 'success' | 'pending' | 'failed';
  createTime: string;
}

const FinanceManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);

  // 模拟数据
  const mockData: FinanceRecord[] = [
    {
      key: '1',
      id: 'FIN001',
      recordType: 'income',
      amount: 299.9,
      description: '企业专线套餐收入',
      merchantName: '北京科技有限公司',
      orderNumber: '202401150002',
      paymentMethod: '支付宝',
      status: 'success',
      createTime: '2024-01-15 11:15:00',
    },
    {
      key: '2',
      id: 'FIN002',
      recordType: 'income',
      amount: 29.9,
      description: '基础流量包收入',
      merchantName: '张三',
      orderNumber: '202401150001',
      paymentMethod: '微信支付',
      status: 'success',
      createTime: '2024-01-15 10:30:00',
    },
    {
      key: '3',
      id: 'FIN003',
      recordType: 'refund',
      amount: -99.9,
      description: '高级流量包退款',
      merchantName: '赵六',
      orderNumber: '202401140001',
      paymentMethod: '微信支付',
      status: 'success',
      createTime: '2024-01-14 16:45:00',
    },
    {
      key: '4',
      id: 'FIN004',
      recordType: 'expense',
      amount: -50.0,
      description: '系统维护费用',
      merchantName: '系统',
      orderNumber: '-',
      paymentMethod: '银行转账',
      status: 'success',
      createTime: '2024-01-13 09:00:00',
    },
  ];

  const columns = [
    {
      title: '记录类型',
      dataIndex: 'recordType',
      key: 'recordType',
      render: (type: string) => {
        const typeMap = {
          income: { text: '收入', color: 'green' },
          expense: { text: '支出', color: 'red' },
          refund: { text: '退款', color: 'blue' },
        };
        const typeInfo = typeMap[type as keyof typeof typeMap];
        return <Tag color={typeInfo.color}>{typeInfo.text}</Tag>;
      },
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => (
        <span style={{ color: amount >= 0 ? '#52c41a' : '#f5222d', fontWeight: 'bold' }}>
          {amount >= 0 ? '+' : ''}¥{amount.toFixed(2)}
        </span>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '商户/客户',
      dataIndex: 'merchantName',
      key: 'merchantName',
    },
    {
      title: '订单号',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
    },
    {
      title: '支付方式',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap = {
          success: { text: '成功', color: 'green' },
          pending: { text: '处理中', color: 'orange' },
          failed: { text: '失败', color: 'red' },
        };
        const statusInfo = statusMap[status as keyof typeof statusMap];
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
    },
    {
      title: '操作',
      key: 'action',
      render: (record: FinanceRecord) => (
        <Space size="middle">
          <Button type="link" size="small">查看</Button>
          <Button type="link" size="small">详情</Button>
        </Space>
      ),
    },
  ];

  const handleSearch = (value: string) => {
    console.log('搜索:', value);
  };

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  // 计算统计数据
  const totalIncome = mockData
    .filter(item => item.recordType === 'income')
    .reduce((sum, item) => sum + item.amount, 0);
  
  const totalExpense = mockData
    .filter(item => item.recordType === 'expense')
    .reduce((sum, item) => sum + Math.abs(item.amount), 0);
  
  const totalRefund = mockData
    .filter(item => item.recordType === 'refund')
    .reduce((sum, item) => sum + Math.abs(item.amount), 0);
  
  const netIncome = totalIncome - totalExpense - totalRefund;

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>财务管理</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<DownloadOutlined />}>
                导出报表
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                刷新
              </Button>
            </Space>
          </Col>
        </Row>

        {/* 统计卡片 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总收入"
                value={totalIncome}
                precision={2}
                valueStyle={{ color: '#3f8600' }}
                prefix={<DollarOutlined />}
                suffix="元"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="总支出"
                value={totalExpense}
                precision={2}
                valueStyle={{ color: '#cf1322' }}
                prefix={<DollarOutlined />}
                suffix="元"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="总退款"
                value={totalRefund}
                precision={2}
                valueStyle={{ color: '#1890ff' }}
                prefix={<DollarOutlined />}
                suffix="元"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="净收入"
                value={netIncome}
                precision={2}
                valueStyle={{ color: netIncome >= 0 ? '#3f8600' : '#cf1322' }}
                prefix={<DollarOutlined />}
                suffix="元"
              />
            </Card>
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Col>
            <Space>
              <Search
                placeholder="搜索描述、商户名称、订单号"
                allowClear
                enterButton={<SearchOutlined />}
                style={{ width: 300 }}
                onSearch={handleSearch}
              />
              <Select placeholder="记录类型" style={{ width: 120 }} allowClear>
                <Option value="income">收入</Option>
                <Option value="expense">支出</Option>
                <Option value="refund">退款</Option>
              </Select>
              <Select placeholder="状态" style={{ width: 120 }} allowClear>
                <Option value="success">成功</Option>
                <Option value="pending">处理中</Option>
                <Option value="failed">失败</Option>
              </Select>
              <RangePicker placeholder={['开始日期', '结束日期']} />
            </Space>
          </Col>
          <Col>
            <Space>
              <Button>批量操作</Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={mockData}
          loading={loading}
          pagination={{
            total: mockData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
        />
      </Card>
    </div>
  );
};

export default FinanceManagement; 